import React, { useEffect, useMemo, useState } from "react"
import { Divider, Layout, Typography, Tooltip } from 'antd';
import { Link } from "react-router-dom";
import UserImageCircle from "../../../components/UserImageCircle"
import { useTalent } from "../../../providers/TalentProvider"
import { useAuth } from "../../../providers/AuthProvider";
import { VideoCameraTwoTone, CheckCircleTwoTone, InfoCircleFilled, InfoCircleOutlined, InfoCircleTwoTone } from "@ant-design/icons";
import CandidateActions from "../../talent/CandidateActions";
import useBottomVisibility from "../../../hooks/useBottomVisibility";
import { useLocation } from "react-use";
import ExpandableHtmlText from "../../../components/ExpandableHtmlText";
import TalentDisplayName from "../../../components/TalentDisplayName";

const { Text } = Typography;
const { Sider } = Layout;

const CommitmentOptions = {
    3: {
        title: 'Full-Time Freelancer',
        description: "I'm seeking independent freelance/contractor assignments as my primary source of income",
    },
    1: {
        title: 'Side Hustler',
        description: "I'm employed and looking for side-hustle freelance assignments (part-time) for supplemental income",
    }
}

const TalentProfileDetails = ({ shouldMinimizeContent, isVisible, onVisible, ...props }) => {
    const { talent } = useTalent();
    const { auth } = useAuth();

    const location = useLocation();
    const {
        containerRef,
        bottomMarkerRef,
        isBottomVisible,
        bottomMarkerStyle = {},
        wasBottomVisibleOnLoad,
        hasCheckedInitialVisibility
    } = useBottomVisibility();


    // Initialize if bottom is visible on load
    useEffect(() => {
        if (hasCheckedInitialVisibility) {
            console.log('InitLoadChange', { isVisible, wasBottomVisibleOnLoad })
            onVisible(wasBottomVisibleOnLoad)
        }
    }, [wasBottomVisibleOnLoad, hasCheckedInitialVisibility]);


    // Send changed when bottom is visible
    useEffect(() => {
        if (hasCheckedInitialVisibility && wasBottomVisibleOnLoad !== isBottomVisible) {
            console.log('AfterLoadChange', { isBottomVisible, isVisible, wasBottomVisibleOnLoad })
            onVisible(isBottomVisible)
        }
    }, [isBottomVisible]);

    // Reset bio and roles to closed after affixed and minimized
    useEffect(() => {
        if (shouldMinimizeContent && (collapsedContent?.bio || collapsedContent.roles)) {
            setCollapsedContent({ bio: false, roles: false })
        }
    }, [shouldMinimizeContent]);

    const [collapsedContent, setCollapsedContent] = useState({ bio: false, roles: false });
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const references = talent.talent_references.filter(ref => ref?.is_confirmed);
    const scoringOptions = laravel.talent_scoring_options;
    const approvedRoles = talent.talent_disciplines?.filter(d => d.is_approved);
    const visibleTalentDisciplines = useMemo(() => {

        // return TB.slice(talent.talent_disciplines || [], 0, (collapsedContent?.roles && !shouldMinimizeContent) ? talent.talent_disciplines?.length : 4)
        return TB.slice(approvedRoles || [], 0, (collapsedContent?.roles && !shouldMinimizeContent) ? approvedRoles?.length : 4)
    }, [shouldMinimizeContent, collapsedContent?.roles]);

    const [backBtnTitle, redirectTo] = useMemo(() => {
        let path = location?.pathname || '';
        let title = 'Go Back';
        let redirect = {
            ...location,
            pathname: path.substring(0, path.indexOf('/talent-profiles'))
        }

        if (TB.includes(path, 'team-profiles/')) {
            title = 'Back to team profile';
        } else if (TB.includes(path, 'agency-profiles/') && TB.includes(path, 'talent-profiles/')) {
            title = 'Back to agency';
        } else if (TB.includes(path, 'browse/')) {
            title = 'Back to browse talent'
        } else if (TB.includes(path, 'talent-pool/')) {
            title = 'Back to talent pool';
        } else if (TB.includes(path, 'shortlist/')) {
            title = 'Back to shortlist'
        }

        if (!redirect.pathname?.length) {
            const canGoBack = window.history.length > 1 && TB.isDefined(location?.key);
            console.log('cangoback', { canGoBack })
            redirect = !canGoBack ? TB.auth().appropriate() : -1
        }


        // else if (TB.auth().InitIntended?.pathname !== location.pathname) {
        //     redirect = -1;
        // }

        return [title, redirect]
    }, [location.pathname]);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 120;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    }

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={isSidebarCollapsed}
            width={448}
            collapsedWidth={190}
            // className="bg-transparent w-full max-w-[unset] min-w-[unset] md:min-w-[448px] md:max-w-[448px]"
            className={TB.mapClass('bg-transparent', {
                'max-w-[unset] min-w-[unset] md:min-w-[448px]': !isSidebarCollapsed,
            })}
        >
            <div ref={containerRef} className="bg-white pt-12 px-4 pb-6 drop-shadow-md">

                <div className="absolute left-4 top-0 flex flex-row items-center gap-2">
                    <Link to={redirectTo} replace={false} className="btn btn-link text-[#323C47] hover:text-[#1A1F25] focus:text-[#1A1F25] no-underline py-0 h-12 gap-1 flex flex-row items-center">
                        <i className="fas fa-chevron-left" />
                        <span>
                            {backBtnTitle}
                        </span>
                    </Link>
                </div>

                <div>
                    <div className="flex justify-between relative">

                        <div className={TB.mapClass("flex items-center flex-grow", {
                            "gap-6": !shouldMinimizeContent && !isSidebarCollapsed,
                            "gap-4": shouldMinimizeContent || isSidebarCollapsed,
                            "flex-col": isSidebarCollapsed,
                        })}>

                            <div className="relative transition-[width] delay-150">
                                <UserImageCircle
                                    className="img-grayscale mx-auto img-fluid transition-all"
                                    user={talent}
                                    hideTooltip
                                    size={(isSidebarCollapsed || shouldMinimizeContent) ? 96 : 128} />
                            </div>

                            <div className="flex flex-col items-start justify-center gap-1 flex-grow">

                                <TalentDisplayName talent={talent}>
                                    <p className={TB.mapClass("font-bold", {
                                        "text-xl": !shouldMinimizeContent && !isSidebarCollapsed,
                                        "text-lg": shouldMinimizeContent || isSidebarCollapsed,
                                        "whitespace-nowrap": !isSidebarCollapsed
                                    })}>
                                        {talent.firstNamePlusInitial()}
                                    </p>
                                </TalentDisplayName>

                                {!isSidebarCollapsed && (
                                    <React.Fragment>
                                        {/*{shouldMinimizeContent &&*/}
                                        {/*    <Text type="secondary" className="text-xs">{talent.job_title}</Text>}*/}

                                        {talent.location && (
                                            <div className="flex items-center my-1 gap-2">
                                                <i className="fas fa-map-marker-alt" />
                                                <p className="text-xs leading-4 text-[#224760]">{talent.location.city}{!!talent.location.state && `, ${talent.location.state}`}</p>
                                            </div>
                                        )}

                                        <div className={TB.mapClass("self-stretch relative", {
                                            "flex flex-row items-center gap-2": shouldMinimizeContent,
                                            "flex flex-col items-stretch gap-1": !shouldMinimizeContent,
                                        })}>
                                            {(talent.displayed_resume_path || talent.linkedin_profile_url || talent.talent_video_interviews?.length > 0) && (
                                                <div className="flex gap-2.5 items-center flex-row justifycenter">
                                                    {talent.linkedin_profile_url && (

                                                        <Tooltip
                                                            placement="bottom"
                                                            color="#000"
                                                            title="Visit LinkedIn Profile"
                                                        >
                                                            <a href={talent.linkedin_profile_url} className="flex flex-col items-center justify-center h-7" target="_blank">
                                                                <i className="fab fa-linkedin text-[26px]" />
                                                            </a>
                                                        </Tooltip>
                                                    )}
                                                    {talent.displayed_resume_path && (
                                                        <Tooltip
                                                            placement="bottom"
                                                            color="#000"
                                                            title="Download Resume"
                                                        >
                                                            <a href={talent.displayed_resume_path} className="flex flex-col items-center justify-center h-7" target="_blank">
                                                                <i className="fas fa-file-alt text-[22px]" />
                                                            </a>
                                                        </Tooltip>
                                                    )}
                                                    {!!talent.talent_video_interviews?.length && (
                                                        <Tooltip
                                                            placement="bottom"
                                                            color="#000"
                                                            title="Interview Videos"
                                                        >
                                                            <button type="button" className="flex flex-col items-center justify-center h-7" onClick={() => scrollToSection('interviews')} >
                                                                <VideoCameraTwoTone twoToneColor={TB.config('color.secondary')} style={{ fontSize: 26 }} />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>
                                )}
                            </div>
                        </div>


                        <button
                            type="button"
                            className="btn-outline bg-wrippleOffWhite h-6 w-6 ml-auto shrink-0 md:flex items-center justify-center hidden"
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                            <i className={TB.mapClass(
                                "fa fa-chevron-left transition text-base", {
                                'rotate-180': isSidebarCollapsed
                            })} />
                        </button>

                    </div>
                    {!isSidebarCollapsed && (
                        <div className="flex flex-col items-stretch">
                            <Divider className="my-5 bordertransparent" />

                            {talent?.supplier_client?.id ? (
                                <div className="flex flex-row items-center gap-2">
                                    <Link
                                        target="_blank" to={`/agency-profiles/${talent.supplier_client.id}`}>
                                        <img alt={''} src={talent?.supplier_client.logo_path} className="rounded-full h-12 w-12 max-h-12 max-w-12 object-contain" />
                                    </Link>
                                    <div className="flex flex-col items-start self-center justify-center">
                                        <p className="text-xxs">
                                            Member of agency:
                                        </p>

                                        <p className="text-sm font-semibold">
                                            {talent.supplier_client.name}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-row items-center gap-2">
                                    <div className="flex gap-1.5 items-center mr-2">
                                        <CheckCircleTwoTone style={{ fontSize: 22 }} twoToneColor={TB.config('color.success-darker')} />
                                        <p className="text-xs font-medium">
                                            Vetted
                                        </p>
                                    </div>

                                    {CommitmentOptions[talent.talent_commitment_level] && (
                                        <Tooltip
                                            placement="bottom"
                                            color="#000"
                                            title={CommitmentOptions[talent.talent_commitment_level]?.description}
                                        >
                                            <div className="self-stretch">
                                                <div className="py-1 px-2 inline-block bg-wripplePrimaryRuleBlue cursor-default">
                                                    <p className="text-xs">{CommitmentOptions[talent.talent_commitment_level]?.title}</p>
                                                </div>
                                            </div>
                                        </Tooltip>
                                    )}
                                    {talent.has1099Classification() && (
                                        <div className="py-1 px-2 bg-wrippleLightSemitransparent">
                                            <p className="text-xs">1099</p>
                                        </div>
                                    )}
                                    {talent.hasW2Classification() && (
                                        <div className="py-1 px-2 bg-wrippleLightSemitransparent">
                                            <p className="text-xs">W2 Hourly</p>
                                        </div>
                                    )}

                                    <div className="ml-auto">
                                        <span onClick={() => scrollToSection('work-status')} className="clickable p-0 m-0 flex">
                                            <InfoCircleFilled className="text-wrippleLightDarkest" style={{ fontSize: 16 }} />
                                        </span>
                                    </div>
                                </div>
                            )}
                            <Divider className="my-5" />
                        </div>
                    )}



                    <div className={TB.mapClass("transition-all duration-75", { 'opacity-0 invisible h-0': isSidebarCollapsed })}>
                        <div className={TB.mapClass("flex flex-col gap-5 transition-all delay-150", { 'opacity-0 invisible': isSidebarCollapsed })}>

                            {!shouldMinimizeContent && (
                                <div>
                                    <p className="text-lg font-medium breakall">{talent.job_title}</p>

                                    <div className="flex items-end gap-3 mt-1">
                                        <ExpandableHtmlText
                                            shouldExpand={collapsedContent?.bio}
                                            onChange={() => setCollapsedContent({ ...collapsedContent, bio: !collapsedContent?.bio })}
                                            showReadMore={false}
                                            content={TB.stripHtml(talent.bio || '')?.length > 0 ? talent.bio : '<i>No Background Provided</i>'}
                                            maxHeight={150}
                                        >
                                            <button
                                                type="button"
                                                className="btn-outline bg-wrippleOffWhite h-6 w-6 shrink-0 flex items-center justify-center"
                                                onClick={() => {
                                                    const visible = !collapsedContent?.bio;
                                                    setCollapsedContent({ ...collapsedContent, bio: !collapsedContent?.bio })
                                                    onVisible(visible)
                                                }}
                                            >
                                                <i className={TB.mapClass("fa fa-chevron-down transition text-base", { 'rotate-180': collapsedContent?.bio })} />
                                            </button>
                                        </ExpandableHtmlText>
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className={TB.mapClass("bg-wrippleLightLight -mx-4 py-4 flex flex-col gap-3", {
                                    "px-4": shouldMinimizeContent,
                                    "pl-4 pr-4": !shouldMinimizeContent,
                                })}>

                                    <CandidateActions minimized={shouldMinimizeContent} />

                                </div>
                            </div>

                            <div>
                                <p className="text-base font-medium pb-2">Roles</p>

                                <div className="flex gap-4 flex-wrap items-start">
                                    {TB.map(visibleTalentDisciplines, discipline => (
                                        <div key={discipline.id}
                                            className="flex flex-col items-center justify-center text-center max-w-52 border border-secondary rounded-lg p-2 pb-1">
                                            <p className="text-xs font-bold text-ellipsis line-clamp-2">{discipline.name}</p>
                                            {!shouldMinimizeContent && (
                                                <p className="text-xxs text-[#3E3F42]">{scoringOptions[discipline.self_score]}</p>
                                            )}
                                        </div>
                                    ))}

                                    {!shouldMinimizeContent && !collapsedContent?.roles && (approvedRoles?.length - 4) > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setCollapsedContent({ ...collapsedContent, roles: !collapsedContent?.roles })}
                                            className="self-stretch text-center border border-secondary flex items-center justify-center rounded-lg py-2 px-5"
                                        >
                                            <p className="text-xs font-bold line-clamp-2 overflow-ellipsis">+ {approvedRoles?.length - 4}</p>
                                        </button>
                                    )}
                                    {shouldMinimizeContent && (approvedRoles?.length - 4) > 0 && (
                                        <p
                                            className="text-xxs mr-auto text-left self-center"
                                        >
                                            (+ {approvedRoles?.length - 4} more)
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/*<div>*/}
                            {/*    <p className="text-base font-medium pb-2">Links</p>*/}

                            {/*    */}
                            {/*</div>*/}

                            {!!(references.length || talent.projects.length) && (
                                <div>
                                    <p className="text-base font-medium pb-2">Badges</p>

                                    <div className="flex flex-col gap-2">

                                        {!!references.length && (
                                            <div className="flex gap-3">
                                                <div className="bg-[#C6FFBE] h-5 w-5 flex items-center justify-center">{references.length}</div>
                                                <p className="text-sm text-[#666666]">{TB.pluralize('Reference', references.length)}</p>
                                            </div>
                                        )}
                                        {!!talent.projects.length && (
                                            <div className="flex gap-3">
                                                <div className="bg-[#C6FFBE] h-5 w-5 flex items-center justify-center">{talent.projects.length}</div>
                                                <p className="text-sm text-[#666666]">Wripple {TB.pluralize('Projects', talent.projects.length)} Completed</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* {!shouldMinimizeContent && !!talent.portfolio_url && ( */}
                            {!!talent.portfolio_url && (
                                <div>
                                    <p className="text-base font-medium pb-2">Portfolio</p>

                                    <div className="flex items-center gap-1.5">
                                        <div className="flex items-center justify-center w-6">
                                            <i className="far fa-address-card text-lg" />
                                        </div>
                                        <a href={talent.portfolio_url} target="_blank" className="p-0 btn-link-info underline text-xs line-clamp-1">
                                            {talent.portfolio_url}
                                        </a>
                                        {/*<InfoCircleFilled id="portfolio_notes" className="text-wrippleLightDarkest" style={{ fontSize: 16 }} />*/}
                                    </div>
                                    {talent.portfolio_url_notes?.length > 0 && (
                                        <span title={talent.portfolio_url_notes}
                                            className="mt-1.5 self-start text-xxs flex flex-row items-start bg-gray-100 gap-1.5 px-2 py-1 rounded">
                                            <div className="flex flex-col items-center justify-center h-4">
                                                <InfoCircleOutlined id="portfolio_notes" color={'#343741'} />
                                            </div>
                                            <span>
                                                <b><i>Access Notes: </i></b>{talent.portfolio_url_notes}
                                            </span>

                                        </span>
                                    )}
                                </div>
                            )}


                            {!shouldMinimizeContent && !!talent.profile_links.length && (
                                <div>
                                    <p className="text-base font-medium pb-2">Links</p>

                                    <div className="flex flex-col gap2">
                                        {talent.profile_links.map(link => {
                                            return (
                                                <div key={link.id} className="flex flex-row items-center gap-1.5">
                                                    <div className="flex flex-row items-center gap-2">
                                                        {link.type_icon ? (
                                                            <div className="flex items-center justify-center w-6">
                                                                <i
                                                                    className={TB.mapClass(`fa2x text-xl fa-${link.type_icon}`, {
                                                                        "fab": link.type_icon.includes('fa-brand'),
                                                                        "fas": !link.type_icon.includes('fa-brand')
                                                                    })} style={{ color: link.type_color }}
                                                                />
                                                            </div>
                                                        ) : <i className="fa fa-link text-xxs text-gray-400" />}

                                                        {/* <p className="text-xs font-medium">{link.type_name || 'Link'}</p> */}
                                                    </div>

                                                    <a href={link?.url} target="_blank" className="p-0 btn-link-info underline text-xs line-clamp-1">{link?.url}</a>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}


                            {!shouldMinimizeContent && !!talent.profile_documents.length && (
                                <div>
                                    <p className="text-base font-medium pb-2">Documents</p>

                                    <div className="flex flex-col">
                                        {talent.profile_documents.map(document => {
                                            return (
                                                <div key={document.id} className="flex items-center gap-1.5">
                                                    <div className="flex items-center justify-center w-6">
                                                        <i className="far fa-file-alt text-xl" />
                                                    </div>
                                                    <a href={document?.url} target="_blank" className="p-0 btn-link-info underline text-xs line-clamp-1">{document?.name}</a>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

                {/* Bottom ref to check that entire content has been seen */}
                <div
                    ref={bottomMarkerRef}
                    className="absolute bottom-0 left-0 w-full h-[0.05rem]"
                    style={bottomMarkerStyle}
                />
            </div>
        </Sider>
    )
}

export default TalentProfileDetails;